import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom"; //useSearchParams useNavigate
import { FadeLoader } from "react-spinners";
import { usePostFileTakeQuestMutation } from "../../../services/apiSlice"; //usePostEndQuestMutation
import Button from "../../../components/common/Button/Button";
import CommonModal from "../Modal/ModalCommon";
import images from "../../../images";
import LoadingSpinner from "../Loading/LoadingSpinner";
import { cn } from "../../../Utils/helper";
import { usePostHog } from "../../../hooks/usePostHog";

// Constants for reusability
const FILE_ICONS = {
  image: images.IconPNGImage,
  pdf: images.IconPDF,
  default: images.DefaultUploadImage,
};

// const MODAL_CONTENT = {
//     INVALID_RECEIPT: {
//         titleKey: "quest.detailMultipleChoice.somethingWentWrong",
//         messageKeys: ["quest.detailMultipleChoice.purchaseRejected"],
//         buttonTextKey: "quest.detailMultipleChoice.proceedAnyway",
//     },
//     INVALID_RESOLUTION: {
//         titleKey: "quest.detailMultipleChoice.somethingWentWrong",
//         messageKeys: ["quest.detailMultipleChoice.theResolution"],
//         buttonTextKey: "quest.detailMultipleChoice.reUpload",
//     },
//     INVALID_BUSINESS_NAME: {
//         titleKey: "quest.detailMultipleChoice.somethingWentWrong",
//         messageKeys: ["quest.detailMultipleChoice.businessNotMatched"],
//         buttonTextKey: "quest.detailMultipleChoice.proceedAnyway",
//     },
// };

const STYLES = {
  container:
    "flex flex-col items-center text-xs max-w-[480px] text-zinc-500 relative ",
  label:
    "flex flex-col justify-center items-center w-full bg-white rounded-md min-h-[92px] cursor-pointer relative border-2 border-dashed mt-4",
  button: {
    base: "flex-1 shrink gap-2.5 px-5 py-2 rounded-lg w-full text-white text-base font-medium border-none mt-[18px]",
    disabled: "bg-[#ECEEFF] cursor-not-allowed",
    enabled: "bg-[#253FF6] hover:bg-indigo-700",
  },
  modal: {
    title: "text-[14px] font-medium text-[#253FF6]",
    message:
      "flex flex-col gap-4 text-center items-center text-[#333] text-lg font-medium",
    subMessage: "text-center text-sm text-[#A0A0A0] font-medium px-5",
  },
};

function UploadComponent({
  label = "Upload File",
  onFileUpload,
  maxFiles = 5,
  initialFiles = [],
  handleNextStep = () => {},
  setFileUploaded = () => {},
  // validateReceiptSubmit = true,
}) {
  const [files, setFiles] = useState([]);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState(null);
  const [modalContent, setModalContent] = useState(null);
  const [resolution, setResolution] = useState(true);

  const { t } = useTranslation();
  const modalRef = useRef(null);
  const { id } = useParams();
  const { trackEvent } = usePostHog();

  const [postFileTakeQuest, { isLoading: uploadLoading }] =
    usePostFileTakeQuestMutation();
  // const [postEndQuest] = usePostEndQuestMutation();

  // Use useRef to track files for cleanup
  const filesRef = useRef(files);

  // Update filesRef whenever files changes
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    setFiles([]);
    setConfirmDeleteFile(null);

    if (Array.isArray(initialFiles) && initialFiles.length > 0) {
      const processedFiles = initialFiles.map((file, index) => {
        // Check if it's a File object
        const isFileObject = file instanceof File;

        if (isFileObject) {
          return {
            id: `file_${index}_${Date.now()}`,
            name: file.name,
            type: "image",
            file: file,
            preview: URL.createObjectURL(file),
            isLocal: true,
            serverData: null,
          };
        }

        let fileName = "";
        let previewUrl = null;
        let serverData = file;
        if (file?.name) {
          fileName = file.name;
        } else if (file?.file?.name) {
          fileName = file.file.name;
        } else if (file?.file_name) {
          fileName = file.file_name;
        } else if (file?.filename) {
          fileName = file.filename;
        } else {
          fileName = `File_${index + 1}`;
        }
        if (file?.file_path) {
          previewUrl = `${process.env.REACT_APP_URL_IMAGE}/${file.file_path}`;
        } else if (file?.url) {
          previewUrl = file.url;
        } else if (file?.file?.file_path) {
          previewUrl = `${process.env.REACT_APP_URL_IMAGE}/${file.file.file_path}`;
        } else if (file?.file?.url) {
          previewUrl = file.file.url;
        }

        return {
          id: `server_${index}_${Date.now()}`,
          name: fileName,
          type: "image",
          file: null, // Server files don't have File objects
          preview: previewUrl,
          isLocal: false,
          serverData: serverData,
        };
      });

      setFiles(processedFiles);
    }

    // Cleanup function for local preview URLs
    return () => {
      filesRef.current.forEach((file) => {
        if (file.preview && file.isLocal && file.preview.startsWith("blob:")) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [initialFiles]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    // Track receipt file selection
    trackEvent("receipt_file_selected", {
      file_count: selectedFiles.length,
      quest_id: id,
      existing_files: files.length,
      max_files: maxFiles
    });

    const imageFiles = selectedFiles.filter((file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        toast.error(`${file.name} ${t("toast.error.isNotAnImage")}`);
        
        // Track invalid file type
        trackEvent("receipt_file_validation_failed", {
          file_name: file.name,
          file_type: file.type,
          reason: "not_image",
          quest_id: id
        });
      }
      return isImage;
    });

    if (imageFiles.length === 0) {
      trackEvent("receipt_file_selection_cancelled", {
        reason: "no_valid_images",
        attempted_files: selectedFiles.length,
        quest_id: id
      });
      return;
    }

    if (imageFiles.length + files.length > maxFiles) {
      toast.warning(
        `${t("uploadComponent.youCanOnlyUploadAMaximumOf")} ${maxFiles} ${t(
          "uploadComponent.file"
        )}`
      );
      
      // Track file limit exceeded
      trackEvent("receipt_file_limit_exceeded", {
        attempted_files: imageFiles.length,
        existing_files: files.length,
        max_files: maxFiles,
        quest_id: id
      });
      return;
    }

    // Create new files with preview
    const newFiles = imageFiles.map((file, index) => ({
      id: `new_${Date.now()}_${index}`,
      name: file.name,
      type: "image",
      file,
      preview: URL.createObjectURL(file),
      isLocal: true,
      serverData: null,
    }));

    // Add to existing files
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);

    // Track successful file addition
    trackEvent("receipt_files_added", {
      new_files_count: imageFiles.length,
      total_files_count: updatedFiles.length,
      file_names: imageFiles.map(f => f.name),
      file_sizes: imageFiles.map(f => f.size),
      quest_id: id
    });

    // Notify parent component
    if (onFileUpload) {
      const fileObjects = updatedFiles
        .map((f) => f.file || f.serverData)
        .filter(Boolean);
      onFileUpload(fileObjects);
    }

    // Reset input value
    event.target.value = "";
  };

  const handleDeleteFile = (fileId) => {
    const fileToRemove = files.find((file) => file.id === fileId);
    const updatedFiles = files.filter((file) => file.id !== fileId);

    // Track file deletion
    trackEvent("receipt_file_deleted", {
      file_name: fileToRemove?.name,
      file_type: fileToRemove?.type,
      was_local: fileToRemove?.isLocal,
      remaining_files: updatedFiles.length,
      quest_id: id
    });

    setFiles(updatedFiles);
    setConfirmDeleteFile(null);

    // Cleanup preview URL if it's a local file
    if (
      fileToRemove?.preview &&
      fileToRemove.isLocal &&
      fileToRemove.preview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    // Notify parent component
    if (onFileUpload) {
      const fileObjects = updatedFiles
        .map((f) => f.file || f.serverData)
        .filter(Boolean);
      onFileUpload(fileObjects);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteFile(null);
  };
  const handleReUpload = () => {
    // Track re-upload action
    trackEvent("receipt_re_upload_initiated", {
      previous_file_count: files.length,
      reason: resolution ? "validation_issues" : "resolution_issue",
      quest_id: id
    });

    // Close the modal
    modalRef.current?.closeModal();

    // Clear all files and revoke blob URLs for local files
    files.forEach((file) => {
      if (file.preview && file.isLocal && file.preview.startsWith("blob:")) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setFiles([]);

    // Reset file uploaded state
    setFileUploaded(false);

    // Notify parent component that files have been cleared
    if (onFileUpload) {
      onFileUpload([]);
    }
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      toast.error(t("toast.error.noFilesUploaded"));
      
      // Track submission attempt with no files
      trackEvent("receipt_upload_failed", {
        reason: "no_files",
        quest_id: id
      });
      return;
    }

    // Track receipt upload attempt
    trackEvent("receipt_upload_started", {
      file_count: files.length,
      has_new_files: files.some(f => f.file instanceof File),
      quest_id: id
    });

    try {
      const formData = new FormData();
      files.forEach((fileItem) => {
        if (fileItem.file instanceof File) {
          formData.append("files", fileItem.file);
        }
      });

      const hasNewFiles = files.some((f) => f.file instanceof File);

      if (!hasNewFiles) {
        // Track using existing files
        trackEvent("receipt_upload_success", {
          type: "existing_files",
          file_count: files.length,
          quest_id: id
        });
        
        setFileUploaded(true);
        handleNextStep();
        return;
      }

      const response = await postFileTakeQuest({
        formData,
        participant_id: id,
      }).unwrap();

      if (
        response.status === 200 &&
        response.data.is_resolution === true &&
        response.data.is_name === true &&
        response.data.is_time === true &&
        response.data.is_location === true
      ) {
        // Track successful receipt validation
        trackEvent("receipt_upload_success", {
          type: "new_upload",
          file_count: files.length,
          validation_results: {
            is_resolution: response.data.is_resolution,
            is_name: response.data.is_name,
            is_time: response.data.is_time,
            is_location: response.data.is_location
          },
          quest_id: id
        });

        setResolution(response.data.is_resolution);
        setFileUploaded(true);
        handleNextStep();
      } else {
        const { is_resolution, is_name, is_time, is_location } = response.data;
        const errorMessages = [];

        // Track receipt validation issues
        trackEvent("receipt_validation_failed", {
          file_count: files.length,
          validation_results: {
            is_resolution,
            is_name,
            is_time,
            is_location
          },
          failed_validations: [
            !is_resolution && "resolution",
            !is_name && "name",
            !is_time && "time", 
            !is_location && "location"
          ].filter(Boolean),
          quest_id: id
        });

        if (is_resolution === false) {
          // Only show resolution error and skip other validations
          setResolution(false);
          setModalContent({
            titleKey: "quest.detailMultipleChoice.somethingWentWrong",
            primaryMessageKeys: ["quest.detailMultipleChoice.theResolution"],
            buttonTextKey: "quest.detailMultipleChoice.proceedAnyway",
          });
        } else {
          setResolution(true);
          // Only validate other fields if resolution is true
          if (is_name === false) {
            errorMessages.push("quest.detailMultipleChoice.businessNotMatched");
          }
          if (is_location === false) {
            errorMessages.push(
              "quest.detailMultipleChoice.purchaseRejectedLocation"
            );
          }
          if (is_time === false) {
            errorMessages.push("quest.detailMultipleChoice.purchaseRejected");
          }
          setModalContent({
            titleKey: "quest.detailMultipleChoice.somethingWentWrong",
            primaryMessageKeys: errorMessages,
            buttonTextKey: "quest.detailMultipleChoice.proceedAnyway",
          });
        }

        if (errorMessages.length > 0 || is_resolution === false) {
          modalRef.current?.openModal();
        }
      }
    } catch (error) {
      // Track upload failure
      trackEvent("receipt_upload_failed", {
        reason: "api_error",
        error_message: error?.data?.message || error?.message,
        file_count: files.length,
        quest_id: id
      });

      const errorMessage =
        error?.data?.message || t("toast.error.noFilesUploaded");
      toast.error(errorMessage);
      console.error("Upload failed:", error);
    }
  };

  return (
    <div className={STYLES.container}>
      {files.length === 0 ? (
        <label className={STYLES.label}>
          <input
            type="file"
            className="hidden"
            onChange={handleFileChange}
            multiple
            accept="image/*"
          />
          <div className="flex flex-col items-center py-4 w-full mt-2">
            <img
              loading="lazy"
              src={FILE_ICONS.default}
              className="object-contain w-[26px]"
              alt="Default icon"
            />
            <div className="gap-1.5 self-stretch my-auto mt-1.5 flex justify-center text-[#A0A0A0] text-sm font-medium">
              {label}
            </div>
          </div>
        </label>
      ) : null}

      {files.length === 0 ? (
        <div className="flex flex-col items-center"></div>
      ) : (
        <div className="w-full mt-5 flex flex-col gap-3">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex flex-col border-[1px] border-[#EEEAE8] relative rounded-[10px] overflow-hidden"
            >
              {confirmDeleteFile === file.id ? (
                <div className="absolute inset-0 bg-black/50 flex flex-col justify-center items-center z-10 p-4 gap-2">
                  <span className="text-white text-base font-medium text-center">
                    {t("quest.detailMultipleChoice.confirmDelete")}
                  </span>
                  <div className="flex gap-2 w-full justify-center">
                    <Button
                      text={t("base.delete")}
                      onClick={() => handleDeleteFile(file.id)}
                      className="flex-1 shrink gap-2.5 px-5 py-2 rounded-xl max-w-[110px] bg-white text-[#253FF6] text-base font-medium"
                    />
                    <Button
                      text={t("base.cancel")}
                      className="flex-1 shrink gap-2.5 px-5 py-2 rounded-xl max-w-[110px] text-white bg-[#253FF6] text-base font-medium border-none"
                      variant="primary"
                      onClick={handleCancelDelete}
                    />
                  </div>
                </div>
              ) : (
                <div className="absolute w-full flex justify-center top-8 z-10">
                  <img
                    src={images.TrashWhiteIcon}
                    onClick={() => setConfirmDeleteFile(file.id)}
                    className="text-white rounded-full p-1 w-8 flex items-center justify-center cursor-pointer"
                    alt="Delete icon"
                  />
                </div>
              )}

              <div className="relative">
                {file.preview ? (
                  <>
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-full max-h-[100px] object-cover rounded-md"
                      onError={(e) => {
                        console.error("Image load error:", e, "File:", file);
                        e.target.style.display = "none";
                        const filenameElement = e.target.nextElementSibling;
                        if (filenameElement) {
                          filenameElement.style.display = "block";
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center rounded-md"></div>
                  </>
                ) : null}
                <div
                  className="p-3 text-center text-sm text-gray-600 bg-gray-50"
                  style={{
                    display: file.preview ? "none" : "block",
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={FILE_ICONS.image}
                      alt="File icon"
                      className="w-8 h-8"
                    />
                    <span className="break-all">{file.name}</span>
                    <span className="text-xs text-gray-400">
                      {file.isLocal ? "Local file" : "Uploaded file"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleSubmit}
        className={`${STYLES.button.base} ${
          uploadLoading ? STYLES.button.disabled : STYLES.button.enabled
        }`}
        disabled={uploadLoading}
      >
        {uploadLoading ? (
          <span className="flex items-center justify-center py-3 px-[18px]">
            <LoadingSpinner size="xs" />
          </span>
        ) : (
          <span className="text-2xl">{t("base.submit")}</span>
        )}
      </button>

      <CommonModal
        ref={modalRef}
        classNameText={STYLES.modal.title}
        showCloseIcon={true}
        closeIcon={images.CloseModalIconGray}
        actions={() => (
          <div className="flex justify-center w-full">
            {uploadLoading ? (
              <FadeLoader color="#3534ff" loading={true} size={15} />
            ) : (
              modalContent && (
                <span
                  onClick={handleNextStep}
                  className="text-[#A0A0A0] font-[Figtree] text-[16px] font-medium leading-[24px] underline  cursor-pointer"
                >
                  {t(modalContent.buttonTextKey)}
                </span>
              )
            )}
          </div>
        )}
      >
        <div
          className={cn(
            modalContent?.primaryMessageKeys?.length > 1
              ? "text-sm sm:text-base md:text-lg flex flex-col items-center"
              : "flex flex-col items-center gap-6 w-full"
          )}
        >
          <img
            src={images.ReviewReceiptImage}
            alt="Receipt Illustration"
            className={cn(
              modalContent?.primaryMessageKeys?.length > 1
                ? "w-[120px]"
                : "w-[170px]"
            )}
          />
          <p className="text-[#253FF6] text-center font-[Figtree] text-[24px] font-medium leading-normal">
            {modalContent
              ? t(modalContent.titleKey)
              : t("quest.questDetails.yourMission")}
          </p>

          {resolution === false ? (
            <>
              <h6 className="text-[#333333] text-center font-[Figtree] text-[18px] font-bold leading-[28px] leading-trim-both">
                {t("quest.detailMultipleChoice.theResolution")}
              </h6>
            </>
          ) : (
            <>
              <h6 className="text-[#333333] text-center font-[Figtree] text-[18px] font-bold leading-[28px] leading-trim-both">
                {modalContent?.primaryMessageKeys?.length} Issue found
              </h6>

              {modalContent?.primaryMessageKeys?.map((key, index) => (
                <div
                  key={index}
                  className="flex flex-row justify-start w-full px-8 gap-2 items-start"
                >
                  <h6 className="text-[#333333] text-start font-[Figtree] text-[18px] font-normal leading-[28px] ">
                    {index + 1}.
                  </h6>
                  <h6 className="text-[#333333] text-start font-[Figtree] text-[18px] font-normal leading-[28px] ">
                    {t(key)}
                  </h6>
                </div>
              ))}
            </>
          )}
          <Button
            onClick={handleReUpload}
            text={"Re-upload"}
            className="w-full px-[18px] py-3 rounded-md min-h-[54px] bg-[#253FF6]  text-white font-[Figtree] text-[20px] font-medium leading-[28px]"
          />

          <p className="text-[#A0A0A0] text-center font-[Figtree] text-[14px] font-medium leading-[21px]">
            {t("quest.detailMultipleChoice.reportIfIncorrect")}
          </p>
        </div>
      </CommonModal>
    </div>
  );
}

UploadComponent.propTypes = {
  label: PropTypes.string,
  onFileUpload: PropTypes.func,
  maxFiles: PropTypes.number,
  initialFiles: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.instanceOf(File),
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        file: PropTypes.instanceOf(File),
        file_path: PropTypes.string,
      }),
    ])
  ),
  handleNextStep: PropTypes.func,
  setFileUploaded: PropTypes.func,
};

export default UploadComponent;
